import React from "react";
import DealProgressIndicator from "./DealProgressIndicator";
import {
  formatCurrency,
  formatDate,
  formatDateTime,
  formatRole,
} from "./dealFormat";

const milestoneStatusLabel = (status) => {
  const labels = {
    pending: "Pending",
    in_progress: "In Progress",
    completed: "Completed",
    skipped: "Skipped",
    blocked: "Blocked",
  };
  return labels[status] || status;
};

// The deal summary, contacts and workback plan — shared by the realtor and
// admin deal detail pages.
const DealSummarySections = ({ deal, contacts, milestones }) => (
  <>
    {/* Deal Summary */}
    <div className="deal-details-section">
      <h3 className="deal-details-section-title">
        {deal.display_address || deal.property_address}
      </h3>
      <div className="deal-details-progress">
        <DealProgressIndicator status={deal.status} />
      </div>
      <div className="deal-details-grid">
        <div className="deal-details-field">
          <span className="deal-details-label">Buyer</span>
          <span className="deal-details-value">{deal.buyer_name || "N/A"}</span>
        </div>
        <div className="deal-details-field">
          <span className="deal-details-label">Seller</span>
          <span className="deal-details-value">{deal.seller_name || "N/A"}</span>
        </div>
        <div className="deal-details-field">
          <span className="deal-details-label">Side</span>
          <span className="deal-details-value">{deal.side || "N/A"}</span>
        </div>
        <div className="deal-details-field">
          <span className="deal-details-label">Offer Price</span>
          <span className="deal-details-value">
            {formatCurrency(deal.offer_price)}
          </span>
        </div>
        <div className="deal-details-field">
          <span className="deal-details-label">Earnest Money</span>
          <span className="deal-details-value">
            {formatCurrency(deal.earnest_money)}
          </span>
        </div>
        <div className="deal-details-field">
          <span className="deal-details-label">Target Date</span>
          <span className="deal-details-value">
            {formatDate(deal.target_date)}
          </span>
        </div>
        <div className="deal-details-field">
          <span className="deal-details-label">Program Status</span>
          <span className="deal-details-value">
            {deal.program_status ? formatRole(deal.program_status) : "N/A"}
          </span>
        </div>
        <div className="deal-details-field">
          <span className="deal-details-label">Created</span>
          <span className="deal-details-value">
            {formatDate(deal.created_at)}
          </span>
        </div>
      </div>
    </div>

    {/* Contacts */}
    <div className="deal-details-section">
      <h4 className="deal-details-section-title">Contacts</h4>
      {contacts.length === 0 ? (
        <p className="deal-details-empty">No contacts available for this deal.</p>
      ) : (
        <div className="deal-details-contacts-grid">
          {contacts.map((contact) => (
            <div key={contact.id} className="deal-details-contact-card">
              <div className="deal-details-contact-role">
                {formatRole(contact.role)}
              </div>
              <div className="deal-details-contact-name">
                {contact.name || "N/A"}
              </div>
              {contact.email && (
                <div className="deal-details-contact-info">{contact.email}</div>
              )}
              {contact.phone && (
                <div className="deal-details-contact-info">{contact.phone}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>

    {/* Workback Milestones */}
    <div className="deal-details-section">
      <h4 className="deal-details-section-title">Workback Plan</h4>
      {milestones.length === 0 ? (
        <p className="deal-details-empty">
          No workback plan available yet. The plan will appear once the deal
          program manager creates it.
        </p>
      ) : (
        <div className="deal-details-milestones">
          {milestones.map((milestone) => (
            <div
              key={milestone.id}
              className={`deal-details-milestone deal-details-milestone-${milestone.status}`}
            >
              <div className="deal-details-milestone-header">
                <span className="deal-details-milestone-name">
                  {milestone.name}
                </span>
                <span
                  className={`deal-details-milestone-status deal-details-milestone-status-${milestone.status}`}
                >
                  {milestoneStatusLabel(milestone.status)}
                </span>
              </div>
              <div className="deal-details-milestone-body">
                <div className="deal-details-milestone-row">
                  <span className="deal-details-label">Assignee</span>
                  <span className="deal-details-value">
                    {milestone.assignee_name || "Unassigned"}
                    {milestone.assignee_email && ` (${milestone.assignee_email})`}
                  </span>
                </div>
                <div className="deal-details-milestone-row">
                  <span className="deal-details-label">Due Date</span>
                  <span className="deal-details-value">
                    {formatDate(milestone.due_date)}
                  </span>
                </div>
                <div className="deal-details-milestone-row">
                  <span className="deal-details-label">Deliverable</span>
                  <span className="deal-details-value">
                    {milestone.deliverable}
                  </span>
                </div>
                {milestone.ping_count > 0 && (
                  <div className="deal-details-milestone-reminders">
                    <span className="deal-details-label">Reminders</span>
                    <span className="deal-details-value">
                      {milestone.ping_count} sent
                      {milestone.reminder_sent_at &&
                        ` (last: ${formatDateTime(milestone.reminder_sent_at)})`}
                    </span>
                  </div>
                )}
                {milestone.completed_at && (
                  <div className="deal-details-milestone-row">
                    <span className="deal-details-label">Completed</span>
                    <span className="deal-details-value">
                      {formatDateTime(milestone.completed_at)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  </>
);

export default DealSummarySections;
